import { cn } from "@/common/helpers/utils";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { AccentScheme, Theme } from "@/store/user-config-store";
import { useTranslation } from "react-i18next";
import type { AccentSchemeDefinition } from "../_constants/themes";

interface AccentSchemePickerProps {
  label: string;
  schemes: AccentSchemeDefinition[];
  selectedScheme: AccentScheme;
  onSelect: (scheme: AccentScheme) => void;
  mode: Theme;
}

export function AccentSchemePicker({ label, schemes, selectedScheme, onSelect, mode }: AccentSchemePickerProps) {
  const { t } = useTranslation("user-settings");

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(100px,1fr))] gap-3">
        {schemes.map((scheme) => {
          const primaryColor = (mode === Theme.Dark ? scheme.primaryColorDark : undefined) ?? scheme.primaryColor;
          return (
            <Card
              key={scheme.scheme}
              depth={1}
              className={cn(
                "cursor-pointer items-center gap-1.5 p-3 transition-all",
                selectedScheme === scheme.scheme
                  ? "border-primary/30 bg-primary/10 dark:border-primary/30 dark:bg-primary/10"
                  : "hover:bg-primary/20 hover:border-primary/60 hover:dark:bg-primary/20 hover:dark:border-primary/60",
              )}
              onClick={() => onSelect(scheme.scheme)}
              role="button"
              tabIndex={0}
              aria-pressed={selectedScheme === scheme.scheme}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onSelect(scheme.scheme);
              }}
            >
              <div className="h-6 w-6 rounded-full border" style={{ backgroundColor: primaryColor }} />
              <span className="text-xs font-medium">{t(scheme.labelKey)}</span>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
